import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazyPage } from '@/lib/lazy-page';
import { AppShell } from '@/components/layout/app-shell';
import { DELIVERED_PHASE, NAVIGATION } from '@/lib/navigation';
import { LoginPage } from '@/pages/auth/login-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { NotFoundPage } from '@/pages/error-pages';
import { ModulePlaceholderPage } from '@/pages/module-placeholder-page';
import { RequireAuth, RequirePermission, RequireSuperAdmin, RequireTenant } from './guards';

// Páginas carregadas sob demanda (code-splitting por rota)
const AdminTenantsPage = lazyPage(
  () => import('@/pages/admin/admin-tenants-page'),
  'AdminTenantsPage',
);
const AuditPage = lazyPage(() => import('@/pages/settings/audit-page'), 'AuditPage');
const CompanySettingsPage = lazyPage(
  () => import('@/pages/settings/company-settings-page'),
  'CompanySettingsPage',
);
const RolesPage = lazyPage(() => import('@/pages/settings/roles-page'), 'RolesPage');
const SettingsIndexPage = lazyPage(
  () => import('@/pages/settings/settings-index-page'),
  'SettingsIndexPage',
);
const UsersPage = lazyPage(() => import('@/pages/settings/users-page'), 'UsersPage');
const RegistrationsSettingsPage = lazyPage(
  () => import('@/pages/settings/registrations-settings-page'),
  'RegistrationsSettingsPage',
);
const PartyFormPage = lazyPage(() => import('@/pages/customers/party-form-page'), 'PartyFormPage');
const PartyListPage = lazyPage(() => import('@/pages/customers/party-list-page'), 'PartyListPage');
const ProductFormPage = lazyPage(
  () => import('@/pages/products/product-form-page'),
  'ProductFormPage',
);
const ProductsListPage = lazyPage(
  () => import('@/pages/products/products-list-page'),
  'ProductsListPage',
);
const MaterialFormPage = lazyPage(
  () => import('@/pages/materials/material-form-page'),
  'MaterialFormPage',
);
const MaterialsListPage = lazyPage(
  () => import('@/pages/materials/materials-list-page'),
  'MaterialsListPage',
);
const InventoryDetailPage = lazyPage(
  () => import('@/pages/stock/inventory-page'),
  'InventoryDetailPage',
);
const InventoryListPage = lazyPage(
  () => import('@/pages/stock/inventory-page'),
  'InventoryListPage',
);
const MovementsPage = lazyPage(() => import('@/pages/stock/movements-page'), 'MovementsPage');
const StockOverviewPage = lazyPage(
  () => import('@/pages/stock/stock-overview-page'),
  'StockOverviewPage',
);
const QuotationsListPage = lazyPage(
  () => import('@/pages/sales/quotations-list-page'),
  'QuotationsListPage',
);
const QuotationFormPage = lazyPage(
  () => import('@/pages/sales/quotation-form-page'),
  'QuotationFormPage',
);
const OrdersListPage = lazyPage(() => import('@/pages/sales/orders-list-page'), 'OrdersListPage');
const SalesOrderPage = lazyPage(() => import('@/pages/sales/order-page'), 'SalesOrderPage');
const PurchasesListPage = lazyPage(
  () => import('@/pages/purchases/purchases-list-page'),
  'PurchasesListPage',
);
const PurchaseOrderPage = lazyPage(
  () => import('@/pages/purchases/purchase-order-page'),
  'PurchaseOrderPage',
);
const ProductionOverviewPage = lazyPage(
  () => import('@/pages/production/production-overview-page'),
  'ProductionOverviewPage',
);
const ProductionListPage = lazyPage(
  () => import('@/pages/production/production-list-page'),
  'ProductionListPage',
);
const ProductionCreatePage = lazyPage(
  () => import('@/pages/production/production-create-page'),
  'ProductionCreatePage',
);
const ProductionDetailPage = lazyPage(
  () => import('@/pages/production/production-detail-page'),
  'ProductionDetailPage',
);
const TimeEntriesPage = lazyPage(
  () => import('@/pages/production/time-entries-page'),
  'TimeEntriesPage',
);
const QualityPage = lazyPage(() => import('@/pages/production/quality-page'), 'QualityPage');
const FinancialOverviewPage = lazyPage(
  () => import('@/pages/financial/financial-overview-page'),
  'FinancialOverviewPage',
);
const TransactionsPage = lazyPage(
  () => import('@/pages/financial/transactions-page'),
  'TransactionsPage',
);
const TitlesPage = lazyPage(() => import('@/pages/financial/titles-page'), 'TitlesPage');
const CashFlowPage = lazyPage(() => import('@/pages/financial/cash-flow-page'), 'CashFlowPage');
const AccountsPage = lazyPage(() => import('@/pages/financial/accounts-page'), 'AccountsPage');
const HelpPage = lazyPage(() => import('@/pages/help/help-page'), 'HelpPage');
const TaxesPage = lazyPage(() => import('@/pages/financial/taxes-page'), 'TaxesPage');
const LoansPage = lazyPage(() => import('@/pages/financial/loans-page'), 'LoansPage');
const MachineRentalsPage = lazyPage(
  () => import('@/pages/machines/machine-rentals-page'),
  'MachineRentalsPage',
);
const MachineInvoicesPage = lazyPage(
  () => import('@/pages/machines/machine-invoices-page'),
  'MachineInvoicesPage',
);
const WasteManifestsPage = lazyPage(
  () => import('@/pages/waste/waste-manifests-page'),
  'WasteManifestsPage',
);
const WasteManifestFormPage = lazyPage(
  () => import('@/pages/waste/waste-manifest-form-page'),
  'WasteManifestFormPage',
);
const FinancialSettingsPage = lazyPage(
  () => import('@/pages/settings/financial-settings-page'),
  'FinancialSettingsPage',
);
const HrOverviewPage = lazyPage(() => import('@/pages/hr/hr-overview-page'), 'HrOverviewPage');
const EmployeesListPage = lazyPage(
  () => import('@/pages/hr/employees-list-page'),
  'EmployeesListPage',
);
const EmployeeFormPage = lazyPage(
  () => import('@/pages/hr/employee-form-page'),
  'EmployeeFormPage',
);
const EmployeeDetailPage = lazyPage(
  () => import('@/pages/hr/employee-detail-page'),
  'EmployeeDetailPage',
);
const EntriesPage = lazyPage(() => import('@/pages/hr/entries-page'), 'EntriesPage');
const LeavesPage = lazyPage(() => import('@/pages/hr/leaves-page'), 'LeavesPage');
const AssetsOverviewPage = lazyPage(
  () => import('@/pages/assets/assets-overview-page'),
  'AssetsOverviewPage',
);
const AssetsListPage = lazyPage(() => import('@/pages/assets/assets-list-page'), 'AssetsListPage');
const AssetFormPage = lazyPage(() => import('@/pages/assets/asset-form-page'), 'AssetFormPage');
const AssetDetailPage = lazyPage(
  () => import('@/pages/assets/asset-detail-page'),
  'AssetDetailPage',
);
const MaintenanceOrdersPage = lazyPage(
  () => import('@/pages/maintenance/maintenance-orders-page'),
  'MaintenanceOrdersPage',
);
const MaintenanceOrderPage = lazyPage(
  () => import('@/pages/maintenance/maintenance-order-page'),
  'MaintenanceOrderPage',
);
const MaintenancePlansPage = lazyPage(
  () => import('@/pages/maintenance/maintenance-plans-page'),
  'MaintenancePlansPage',
);
const InvoicesListPage = lazyPage(
  () => import('@/pages/invoices/invoices-list-page'),
  'InvoicesListPage',
);
const InvoiceFormPage = lazyPage(
  () => import('@/pages/invoices/invoice-form-page'),
  'InvoiceFormPage',
);
const InvoiceDetailPage = lazyPage(
  () => import('@/pages/invoices/invoice-detail-page'),
  'InvoiceDetailPage',
);
const DfePage = lazyPage(() => import('@/pages/invoices/dfe-page'), 'DfePage');
const DocumentsPage = lazyPage(() => import('@/pages/documents/documents-page'), 'DocumentsPage');
const AlertsPage = lazyPage(() => import('@/pages/alerts/alerts-page'), 'AlertsPage');
const LogisticsOverviewPage = lazyPage(
  () => import('@/pages/logistics/logistics-overview-page'),
  'LogisticsOverviewPage',
);
const DeliveriesListPage = lazyPage(
  () => import('@/pages/logistics/deliveries-list-page'),
  'DeliveriesListPage',
);
const DeliveryPage = lazyPage(() => import('@/pages/logistics/delivery-page'), 'DeliveryPage');
const ReportsListPage = lazyPage(
  () => import('@/pages/reports/reports-list-page'),
  'ReportsListPage',
);
const ReportPage = lazyPage(() => import('@/pages/reports/report-page'), 'ReportPage');
const LeadsPage = lazyPage(() => import('@/pages/site/leads-page'), 'LeadsPage');
const SiteCatalogPage = lazyPage(() => import('@/pages/site/site-catalog-page'), 'SiteCatalogPage');
const SiteContentPage = lazyPage(() => import('@/pages/site/site-content-page'), 'SiteContentPage');
const SiteOverviewPage = lazyPage(
  () => import('@/pages/site/site-overview-page'),
  'SiteOverviewPage',
);

/** Rotas de módulos ainda não entregues (entradas do menu com fase > DELIVERED_PHASE). */
const placeholderRoutes = NAVIGATION.filter((n) => n.phase > DELIVERED_PHASE).flatMap((n) => [
  {
    path: n.to,
    element: (
      <RequirePermission anyOf={n.permissions ?? []}>
        <ModulePlaceholderPage />
      </RequirePermission>
    ),
  },
  ...(n.children ?? []).map((c) => ({
    path: c.to,
    element: (
      <RequirePermission anyOf={c.permissions ?? n.permissions ?? []}>
        <ModulePlaceholderPage />
      </RequirePermission>
    ),
  })),
]);

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <Navigate to="/dashboard" replace /> },
          {
            element: <RequireSuperAdmin />,
            children: [
              { path: '/admin', element: <Navigate to="/admin/empresas" replace /> },
              { path: '/admin/empresas', element: <AdminTenantsPage /> },
            ],
          },
          {
            element: <RequireTenant />,
            children: [
              {
                path: '/dashboard',
                element: (
                  <RequirePermission anyOf={['dashboard:read']}>
                    <DashboardPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/configuracoes',
                children: [
                  {
                    index: true,
                    element: (
                      <RequirePermission
                        anyOf={['settings:read', 'users:read', 'roles:read', 'audit:read']}
                      >
                        <SettingsIndexPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'empresa',
                    element: (
                      <RequirePermission anyOf={['settings:read']}>
                        <CompanySettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'cadastros',
                    element: (
                      <RequirePermission anyOf={['products:read', 'materials:read', 'stock:read']}>
                        <RegistrationsSettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'financeiro',
                    element: (
                      <RequirePermission anyOf={['financial:read']}>
                        <FinancialSettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'usuarios',
                    element: (
                      <RequirePermission anyOf={['users:read']}>
                        <UsersPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'permissoes',
                    element: (
                      <RequirePermission anyOf={['roles:read']}>
                        <RolesPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'auditoria',
                    element: (
                      <RequirePermission anyOf={['audit:read']}>
                        <AuditPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
              // Fase 2 — cadastros e estoque
              {
                path: '/clientes',
                element: (
                  <RequirePermission anyOf={['customers:read']}>
                    <PartyListPage kind="customer" />
                  </RequirePermission>
                ),
              },
              {
                path: '/clientes/novo',
                element: (
                  <RequirePermission anyOf={['customers:create']}>
                    <PartyFormPage kind="customer" />
                  </RequirePermission>
                ),
              },
              {
                path: '/clientes/:id',
                element: (
                  <RequirePermission anyOf={['customers:read']}>
                    <PartyFormPage kind="customer" />
                  </RequirePermission>
                ),
              },
              {
                path: '/fornecedores',
                element: (
                  <RequirePermission anyOf={['suppliers:read']}>
                    <PartyListPage kind="supplier" />
                  </RequirePermission>
                ),
              },
              {
                path: '/fornecedores/novo',
                element: (
                  <RequirePermission anyOf={['suppliers:create']}>
                    <PartyFormPage kind="supplier" />
                  </RequirePermission>
                ),
              },
              {
                path: '/fornecedores/:id',
                element: (
                  <RequirePermission anyOf={['suppliers:read']}>
                    <PartyFormPage kind="supplier" />
                  </RequirePermission>
                ),
              },
              {
                path: '/produtos',
                element: (
                  <RequirePermission anyOf={['products:read']}>
                    <ProductsListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/produtos/novo',
                element: (
                  <RequirePermission anyOf={['products:create']}>
                    <ProductFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/produtos/:id',
                element: (
                  <RequirePermission anyOf={['products:read']}>
                    <ProductFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque',
                element: (
                  <RequirePermission anyOf={['stock:read']}>
                    <StockOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/materiais',
                element: (
                  <RequirePermission anyOf={['materials:read']}>
                    <MaterialsListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/materiais/novo',
                element: (
                  <RequirePermission anyOf={['materials:create']}>
                    <MaterialFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/materiais/:id',
                element: (
                  <RequirePermission anyOf={['materials:read']}>
                    <MaterialFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/movimentacoes',
                element: (
                  <RequirePermission anyOf={['stock:read']}>
                    <MovementsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/inventario',
                element: (
                  <RequirePermission anyOf={['stock:read']}>
                    <InventoryListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/estoque/inventario/:id',
                element: (
                  <RequirePermission anyOf={['stock:read']}>
                    <InventoryDetailPage />
                  </RequirePermission>
                ),
              },
              // Fase 3 — comercial e produção
              { path: '/vendas', element: <Navigate to="/vendas/orcamentos" replace /> },
              {
                path: '/vendas/orcamentos',
                element: (
                  <RequirePermission anyOf={['sales:read']}>
                    <QuotationsListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/orcamentos/novo',
                element: (
                  <RequirePermission anyOf={['sales:create']}>
                    <QuotationFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/orcamentos/:id',
                element: (
                  <RequirePermission anyOf={['sales:read']}>
                    <QuotationFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/pedidos',
                element: (
                  <RequirePermission anyOf={['sales:read']}>
                    <OrdersListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/pedidos/novo',
                element: (
                  <RequirePermission anyOf={['sales:create']}>
                    <SalesOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/pedidos/:id',
                element: (
                  <RequirePermission anyOf={['sales:read']}>
                    <SalesOrderPage />
                  </RequirePermission>
                ),
              },
              { path: '/compras', element: <Navigate to="/compras/pedidos" replace /> },
              {
                path: '/compras/pedidos',
                element: (
                  <RequirePermission anyOf={['purchases:read']}>
                    <PurchasesListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/compras/pedidos/novo',
                element: (
                  <RequirePermission anyOf={['purchases:create']}>
                    <PurchaseOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/compras/pedidos/:id',
                element: (
                  <RequirePermission anyOf={['purchases:read']}>
                    <PurchaseOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao',
                element: (
                  <RequirePermission anyOf={['production:read']}>
                    <ProductionOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao/ordens',
                element: (
                  <RequirePermission anyOf={['production:read']}>
                    <ProductionListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao/ordens/nova',
                element: (
                  <RequirePermission anyOf={['production:create']}>
                    <ProductionCreatePage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao/ordens/:id',
                element: (
                  <RequirePermission anyOf={['production:read']}>
                    <ProductionDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao/apontamentos',
                element: (
                  <RequirePermission anyOf={['production:read']}>
                    <TimeEntriesPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/producao/qualidade',
                element: (
                  <RequirePermission anyOf={['quality:read']}>
                    <QualityPage />
                  </RequirePermission>
                ),
              },
              { path: '/ajuda', element: <HelpPage /> },
              {
                path: '/financeiro',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <FinancialOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/entradas',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <TransactionsPage flow="INCOME" />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/saidas',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <TransactionsPage flow="EXPENSE" />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/contas-pagar',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <TitlesPage kind="PAYABLE" />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/contas-receber',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <TitlesPage kind="RECEIVABLE" />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/impostos',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <TaxesPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/emprestimos',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <LoansPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/fluxo-caixa',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <CashFlowPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/financeiro/contas',
                element: (
                  <RequirePermission anyOf={['financial:read']}>
                    <AccountsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <HrOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/colaboradores',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <EmployeesListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/colaboradores/novo',
                element: (
                  <RequirePermission anyOf={['hr:create']}>
                    <EmployeeFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/colaboradores/:id',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <EmployeeDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/colaboradores/:id/editar',
                element: (
                  <RequirePermission anyOf={['hr:update']}>
                    <EmployeeFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/lancamentos',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <EntriesPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/adiantamentos',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <EntriesPage preset="advances" />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/vales',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <EntriesPage preset="vouchers" />
                  </RequirePermission>
                ),
              },
              {
                path: '/rh/ferias',
                element: (
                  <RequirePermission anyOf={['hr:read']}>
                    <LeavesPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio',
                element: (
                  <RequirePermission anyOf={['assets:read']}>
                    <AssetsOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio/equipamentos',
                element: (
                  <RequirePermission anyOf={['assets:read']}>
                    <AssetsListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio/veiculos',
                element: (
                  <RequirePermission anyOf={['assets:read']}>
                    <AssetsListPage vehicles />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio/novo',
                element: (
                  <RequirePermission anyOf={['assets:create']}>
                    <AssetFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio/:id',
                element: (
                  <RequirePermission anyOf={['assets:read']}>
                    <AssetDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/patrimonio/:id/editar',
                element: (
                  <RequirePermission anyOf={['assets:update']}>
                    <AssetFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/manutencoes',
                element: (
                  <RequirePermission anyOf={['maintenance:read']}>
                    <MaintenanceOrdersPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/manutencoes/ordens',
                element: (
                  <RequirePermission anyOf={['maintenance:read']}>
                    <MaintenanceOrdersPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/manutencoes/ordens/:id',
                element: (
                  <RequirePermission anyOf={['maintenance:read']}>
                    <MaintenanceOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/manutencoes/preventivas',
                element: (
                  <RequirePermission anyOf={['maintenance:read']}>
                    <MaintenancePlansPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/horas-maquina',
                element: (
                  <RequirePermission anyOf={['machines:read']}>
                    <MachineRentalsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/horas-maquina/faturas',
                element: (
                  <RequirePermission anyOf={['machines:read']}>
                    <MachineInvoicesPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/mtr',
                element: (
                  <RequirePermission anyOf={['waste:read']}>
                    <WasteManifestsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/mtr/novo',
                element: (
                  <RequirePermission anyOf={['waste:create']}>
                    <WasteManifestFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/mtr/:id',
                element: (
                  <RequirePermission anyOf={['waste:read']}>
                    <WasteManifestFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/notas-fiscais',
                element: (
                  <RequirePermission anyOf={['invoices:read']}>
                    <InvoicesListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/notas-fiscais/nova',
                element: (
                  <RequirePermission anyOf={['invoices:create']}>
                    <InvoiceFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/notas-fiscais/sefaz',
                element: (
                  <RequirePermission anyOf={['invoices:read']}>
                    <DfePage />
                  </RequirePermission>
                ),
              },
              {
                path: '/notas-fiscais/:id',
                element: (
                  <RequirePermission anyOf={['invoices:read']}>
                    <InvoiceDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/documentos',
                element: (
                  <RequirePermission anyOf={['documents:read']}>
                    <DocumentsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/alertas',
                element: (
                  <RequirePermission anyOf={['dashboard:read']}>
                    <AlertsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/logistica',
                element: (
                  <RequirePermission anyOf={['logistics:read']}>
                    <LogisticsOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/logistica/entregas',
                element: (
                  <RequirePermission anyOf={['logistics:read']}>
                    <DeliveriesListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/logistica/entregas/:id',
                element: (
                  <RequirePermission anyOf={['logistics:read']}>
                    <DeliveryPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/relatorios',
                element: (
                  <RequirePermission anyOf={['reports:read']}>
                    <ReportsListPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/relatorios/:key',
                element: (
                  <RequirePermission anyOf={['reports:read']}>
                    <ReportPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/site',
                element: (
                  <RequirePermission anyOf={['settings:read', 'products:read', 'sales:read']}>
                    <SiteOverviewPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/site/conteudo',
                element: (
                  <RequirePermission anyOf={['settings:read']}>
                    <SiteContentPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/site/catalogo',
                element: (
                  <RequirePermission anyOf={['products:read']}>
                    <SiteCatalogPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/site/solicitacoes',
                element: (
                  <RequirePermission anyOf={['sales:read']}>
                    <LeadsPage />
                  </RequirePermission>
                ),
              },
              {
                path: '/vendas/solicitacoes',
                element: <Navigate to="/site/solicitacoes" replace />,
              },
              ...placeholderRoutes,
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
